// @flow
import {action, computed, makeObservable, observable, runInAction} from 'mobx';
// $FlowFixMe
import {Navigate} from 'react-router-dom';
import {Api} from '@wellstone-solutions/common';
import {storage} from 'utils/storage';
import {routes} from 'api';
import {Login} from '@wellstone-solutions/common/models/v2';
import {RootStore} from 'RootStore';
import {MeModel} from './models/me';
import type {UIMeType, UIMembershipType, UIOrganizationType} from './types';

export const AUTH_STORAGE_KEY = 'session_information';

export class AuthStore {
  email: string = '';
  me: UIMeType | null = null;
  rootStore: RootStore | null = null;
  isAuthenticated: boolean = false;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      clearAuth: action,
      email: observable,
      me: observable,
      hasEmail: computed,
      isAuthenticated: observable,
      memberships: computed,
      organizations: computed,
      sendNewCode: action,
      setAuth: action,
      states: computed,
    });

    this.rootStore = rootStore;
  }

  get hasEmail(): boolean {
    return this.email.length > 0;
  }

  get memberships(): UIMembershipType[] {
    if (!this.me) {
      return [];
    }

    return this.me.memberships.filter(
      (membership) => membership.status === 'active',
    );
  }

  get organizations(): UIOrganizationType[] {
    return this.memberships.map(({organization}) => organization);
  }

  get states(): string[] {
    return [
      ...new Set(this.memberships.map(({organization}) => organization.state)),
    ];
  }

  clearAuth() {
    runInAction(() => {
      this.email = '';
      this.me = null;
      this.isAuthenticated = false;
    });

    Api.Instance.v2().setAuthTokens({access: '', refresh: ''});
    storage.removeItem(AUTH_STORAGE_KEY);
  }

  async init() {
    const auth = storage.getItem(AUTH_STORAGE_KEY);

    if (auth) {
      try {
        // $FlowFixMe - storage type doesn't support object
        await this.setAuth(auth);
        await this.getMe();
        // eslint-disable-next-line no-unused-vars
      } catch (errr) {
        this.clearAuth();
        Navigate('/login', {
          replace: true,
        });
      }
    }
  }

  async requestCode(email: string): Promise<void> {
    Login.generateCode(email);

    runInAction(() => {
      this.email = email;
    });
  }

  sendNewCode() {
    this.email = '';
  }

  setAuth({access, refresh}: {access: string, refresh: string}) {
    Api.Instance.v2().setAuthTokens({access, refresh});
    runInAction(() => {
      this.isAuthenticated = true;
    });
  }

  async getMe(): Promise<void> {
    const meResponse = await Api.Instance.current().get(routes.me);
    if (!meResponse.isSuccess) {
      throw new Error('Error fetching user data');
    }
    runInAction(() => {
      this.me = MeModel.toUI(meResponse.data);
    });
  }

  async signIn(code: string): Promise<void> {
    const authResponse = await Login.loginWithCode(this.email, code);
    await this.setAuth(authResponse.data);
    await this.getMe();
    storage.setItem(AUTH_STORAGE_KEY, authResponse.data);
  }

  async signOut(): Promise<void> {
    try {
      await Login.logout();
    } finally {
      this.clearAuth();
      window.location.reload();
    }
  }
}
